/**
 * Two per-browser settings, chosen at runtime so Kai and Gavin can point the
 * deployed site at a real chain without a rebuild:
 *
 *   ?net=     devnet | mainnet | localnet   — which chain the admin panel
 *             signs on and the real player reads. Default devnet.
 *             mainnet is only honoured once the program has a mainnet
 *             address in kit/programs.json; localnet only in a build told
 *             where a local validator is (VITE_SOLANA_RPC_URL — ./stack dev).
 *   ?verify=  fixture | real — whether the public player shows the scripted
 *             verification demo (nothing is checked) or really checks the
 *             video against the chain. Default fixture, while
 *             DEMO_BY_DEFAULT is true.
 *
 * Each: the URL parameter (which is also remembered) → the value remembered in
 * localStorage → the default. Changeable from the Network button too.
 *
 * Resolved ONCE, when this module is first evaluated. Every program address
 * and PDA in the app is a module-level constant derived from it, so changing
 * either means reloading — `choose` does that. Module evaluation happens
 * before the router's `/video/<id>` → `/video/<slug>` redirect drops the query
 * string, so the parameters are seen.
 */
import { type Cluster, isCluster } from './kit/index.js'
import { isDeployed } from './kit/registry.js'

/**
 * TEMPORARY — DEMO MODE. With no choice made, a browser sees the scripted
 * verification UI. Set false once the real path is the one the public should
 * see.
 */
export const DEMO_BY_DEFAULT = true

/** The chain a browser uses when it has not picked one. */
export const DEFAULT_NETWORK: NetworkChoice = 'devnet'

export type NetworkChoice = 'devnet' | 'mainnet' | 'localnet'
export type VerifyMode = 'fixture' | 'real'

export const NET_KEY = 'forum.network.v2'
export const VERIFY_KEY = 'forum.verify.v1'

const CLUSTER_OF: Record<NetworkChoice, Cluster> = {
  devnet: 'devnet',
  mainnet: 'mainnet-beta',
  localnet: 'localnet',
}

export const NET_LABEL: Record<NetworkChoice, string> = {
  devnet: 'Devnet',
  mainnet: 'Mainnet',
  localnet: 'Localnet',
}

export const VERIFY_LABEL: Record<VerifyMode, string> = {
  fixture: 'Demo',
  real: 'Real',
}

/** Accepts `mainnet-beta` too, since that is what the rest of Solana calls it. */
export function parseNet(raw: string | null | undefined): NetworkChoice | null {
  if (!raw) return null
  const v = raw.trim().toLowerCase()
  if (v === 'mainnet-beta') return 'mainnet'
  return v === 'devnet' || v === 'mainnet' || v === 'localnet' ? v : null
}

export function parseVerify(raw: string | null | undefined): VerifyMode | null {
  if (!raw) return null
  const v = raw.trim().toLowerCase()
  return v === 'fixture' || v === 'real' ? v : null
}

export interface NetworkEnv {
  /** Whether a local validator URL was baked into the build. */
  hasLocalnet: boolean
  /** Whether the forum program has an address on a cluster. */
  deployed: (cluster: Cluster) => boolean
  demoByDefault: boolean
  defaultNetwork: NetworkChoice
}

/** Which networks this build can honour, in menu order. */
export function availableNetworks(env: NetworkEnv): { choice: NetworkChoice; enabled: boolean; note?: string }[] {
  const out: { choice: NetworkChoice; enabled: boolean; note?: string }[] = []
  for (const choice of ['devnet', 'mainnet', 'localnet'] as const) {
    if (choice === 'localnet' && !env.hasLocalnet) continue
    const ok = env.deployed(CLUSTER_OF[choice])
    out.push({ choice, enabled: ok, note: ok ? undefined : 'not deployed yet' })
  }
  return out
}

const honoured = (choice: NetworkChoice, env: NetworkEnv): boolean =>
  availableNetworks(env).some((c) => c.choice === choice && c.enabled)

export interface ResolvedNetwork {
  net: NetworkChoice
  /** The chain the admin panel signs on and the real player reads. */
  cluster: Cluster
  verify: VerifyMode
  /** Whether the public player shows the scripted demo. */
  demo: boolean
  /** True when either setting came from the URL or storage rather than the
   * defaults — somebody deliberately picked it, so it is worth showing them. */
  explicit: boolean
  /** A requested network this build cannot honour, for a console warning. */
  rejected: string | null
}

export interface Inputs {
  search: string
  storedNet: string | null
  storedVerify: string | null
}

/** Pure: everything it needs is passed in, so it is testable without a DOM. */
export function resolveNetwork({ search, storedNet, storedVerify }: Inputs, env: NetworkEnv): ResolvedNetwork {
  const params = new URLSearchParams(search)
  let rejected: string | null = null
  let net: NetworkChoice | null = null
  let explicit = false
  for (const raw of [params.get('net'), storedNet]) {
    const parsed = parseNet(raw)
    if (parsed && honoured(parsed, env)) {
      net = parsed
      explicit = true
      break
    }
    if (raw && rejected === null) rejected = raw
  }
  if (!net) net = honoured(env.defaultNetwork, env) ? env.defaultNetwork : 'devnet'

  let verify = parseVerify(params.get('verify')) ?? parseVerify(storedVerify)
  if (verify) explicit = true
  else verify = env.demoByDefault ? 'fixture' : 'real'

  return { net, cluster: CLUSTER_OF[net], verify, demo: verify === 'fixture', explicit, rejected }
}

const safeGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Private window or blocked storage: the choice lasts this page load only.
  }
}

// The build's VITE_SOLANA_CLUSTER is no longer what picks the chain, but a
// typo in it is still a broken build worth failing loudly on.
const buildClusterRaw = import.meta.env.VITE_SOLANA_CLUSTER
if (buildClusterRaw !== undefined && !isCluster(buildClusterRaw)) {
  throw new Error(`VITE_SOLANA_CLUSTER="${buildClusterRaw}" is not a cluster (localnet, devnet, mainnet-beta)`)
}

export const networkEnv: NetworkEnv = {
  hasLocalnet: Boolean(import.meta.env.VITE_SOLANA_RPC_URL),
  deployed: (cluster) => isDeployed('forum', cluster),
  demoByDefault: DEMO_BY_DEFAULT,
  defaultNetwork: DEFAULT_NETWORK,
}

const hasWindow = typeof window !== 'undefined'
const search = hasWindow ? window.location.search : ''

export const network: ResolvedNetwork = resolveNetwork(
  {
    search,
    storedNet: hasWindow ? safeGet(NET_KEY) : null,
    storedVerify: hasWindow ? safeGet(VERIFY_KEY) : null,
  },
  networkEnv,
)

// URL parameters that were honoured are remembered, so they survive the
// router's redirect and every later visit until changed.
if (hasWindow) {
  const params = new URLSearchParams(search)
  if (parseNet(params.get('net')) === network.net) safeSet(NET_KEY, network.net)
  if (parseVerify(params.get('verify')) === network.verify) safeSet(VERIFY_KEY, network.verify)
  if (network.rejected) {
    // eslint-disable-next-line no-console
    console.warn(`[forum] network "${network.rejected}" is not available in this build; using ${network.net}`)
  }
}

/** Remember a new setting and reload, since every address is fixed at load. */
export function choose(change: { net?: NetworkChoice; verify?: VerifyMode }): void {
  if (change.net) safeSet(NET_KEY, change.net)
  if (change.verify) safeSet(VERIFY_KEY, change.verify)
  const url = new URL(window.location.href)
  url.searchParams.delete('net')
  url.searchParams.delete('verify')
  window.location.replace(url.toString())
}
